import { memo, ReactNode, Dispatch } from "react";
import isEqual from "lodash-es/isEqual";
import { Box, Divider, SvgIcon, Typography } from "@mui/material";
// Components
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import SimpleTextField from "@/Layouts/components/InputComponents/SimpleTextField";
import SimpleDoubleChecking from "@/Layouts/components/Buttons/SimpleDoubleChecking";
import AreaColumnInput from "./AreaColumnInput";
// Icons
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { ReactComponent as TrashIcon } from "@/assets/icons/common/trash-icon.svg";
import { ReactComponent as WarningIcon } from "@/assets/icons/svg/warning.svg";
// Utils
import { findError, getFranchiseProps, getFranchiseUpdated } from "@/Settings/helpers/AdministrationTools/LocationDealerCodes/utils";
import {
	TAction,
	EActionType,
	ILocationDealerCode,
	IOemMetaData,
	TErrors,
	IFranchise,
	IArea,
	IPayloadArea,
	TFranchiseSelected,
} from "@/Settings/types/administrationTools/locationDealerCodes";

type FranchisesLayoutsProps = {
	oem: ILocationDealerCode;
	franchise: IFranchise;
	oemMetaData?: IOemMetaData;
	oemIndex: number;
	franchiseIndex: number;
	dispatch: Dispatch<TAction>;
	franchiseSelected: string[];
	errors: TErrors;
	warningPaths: number[][];
	isPending: boolean;
	onSetWarningPaths: (paths: number[], action: string) => void;
};

const FranchisesLayouts = memo(
	({
		oem,
		franchise,
		oemMetaData,
		oemIndex,
		franchiseIndex,
		dispatch,
		franchiseSelected,
		errors,
		warningPaths,
		onSetWarningPaths,
		isPending,
	}: FranchisesLayoutsProps) => {
		const updateFranchiseWarning = warningPaths.some((item) => isEqual(item, [oemIndex, franchiseIndex]));
		const { franchisesNames, metaAreas, singleFranchiseSelected } = getFranchiseProps(franchise, oemMetaData);

		const handleUpdateFranchiseWarning = () => {
			onSetWarningPaths([oemIndex, franchiseIndex], "remove");
			dispatch({
				type: EActionType.UPDATE_FRANCHISE,
				payload: {
					oemIndex,
					franchiseIndex,
				},
			});
		};

		const handleFranchiseSelectorChange = (selected: TFranchiseSelected[]) => {
			const updatedFranchise = getFranchiseUpdated({ franchise, selected, oemMetaData });
			if (updatedFranchise) {
				const action = franchise.franchise && selected.length ? "add" : "remove";
				onSetWarningPaths([oemIndex, franchiseIndex], action);
				dispatch({
					type: EActionType.UPDATE_FRANCHISE,
					payload: {
						oemIndex,
						franchiseIndex,
						...updatedFranchise,
					},
				});
			}
		};

		const handleFranchiseInputTextChange = (param: { franchise?: string; dealerCode?: string }) => {
			dispatch({
				type: EActionType.UPDATE_FRANCHISE,
				payload: {
					oemIndex,
					franchiseIndex,
					...param,
				},
			});
		};

		const handleDeleteFranchise = () => {
			dispatch({
				type: EActionType.DELETE_FRANCHISE,
				payload: {
					oemIndex,
					franchiseIndex,
				},
			});
		};

		const getAreaColumnInput = (areas: IPayloadArea[], area?: IArea, depth = 1): ReactNode => {
			const childArea = areas.find((entry) => entry.areaValue === area?.areaValue) ?? { areas: [] };
			return (
				<>
					<AreaColumnInput
						area={area}
						areas={areas}
						dispatch={dispatch}
						oemIndex={oemIndex}
						franchiseIndex={franchiseIndex}
						errors={errors}
						depth={depth}
						updateFranchiseWarning={updateFranchiseWarning}
						isPending={isPending}
					/>
					{!!childArea?.areas.length && getAreaColumnInput(childArea.areas, area?.area, depth + 1)}
				</>
			);
		};

		return (
			<>
				{franchiseIndex !== 0 && <Divider className="w-[1000px] !my-[32px]" />}
				<Box className="w-[1000px] flex flex-col mb-[8px]">
					<Box className="flex justify-start mb-[24px]">
						<Box className="mr-[12px]">
							<Typography className="regular-montserrat-label pb-[4px] text-center" style={{ fontFamily: "PTSansProNarrow" }}>
								Franchise
							</Typography>
							<SmartSelector
								multiple
								tagsVariant={"text"}
								classes="!w-[220px]"
								showBy="franchiseName"
								sortBy="franchiseName"
								selectBy="franchiseName"
								orderBy="franchiseName"
								options={franchisesNames}
								disabledOptions={[
									...franchiseSelected.filter(
										(item) =>
											franchise.franchise !== item && !franchise.groups.some((group) => group.franchiseName === item)
									),
								]}
								selected={franchise.groups.length ? franchise.groups : singleFranchiseSelected}
								inputProps={{
									placeholder: "Select Franchise",
								}}
								error={
									!!findError(errors, [oemIndex, "franchises", franchiseIndex, "franchise"]) && !franchise.groups.length
								}
								onChangeAction={(selected) => handleFranchiseSelectorChange(selected as TFranchiseSelected[])}
								clearable
								disabled={isPending}
							/>
						</Box>
						{franchise?.isCombined && (
							<Box className="mr-[12px]">
								<Typography
									className="regular-montserrat-label pb-[4px] text-center"
									style={{ fontFamily: "PTSansProNarrow" }}
								>
									Franchise Group Name
								</Typography>
								<SimpleTextField
									classes="!w-[220px]"
									editable
									outlined
									text={franchise?.franchise || ""}
									placeholder={"Franchise"}
									warning={updateFranchiseWarning}
									error={!!findError(errors, [oemIndex, "franchises", franchiseIndex, "franchise"])}
									onActionUpdate={(text) => handleFranchiseInputTextChange({ franchise: text })}
									disabled={isPending}
								/>
							</Box>
						)}
						<Box className="w-[150px] mr-[75px] pb-[8px]">
							<Typography
								className={"regular-montserrat-label pb-[4px] text-center"}
								style={{ fontFamily: "PTSansProNarrow" }}
							>
								Dealer Code
							</Typography>
							<SimpleTextField
								classes="!w-[220px]"
								editable
								outlined
								text={franchise?.dealerCode || ""}
								inputProps={{ maxLength: 8 }}
								placeholder={"i.e. BAC, Dealer Code"}
								warning={updateFranchiseWarning}
								error={!!findError(errors, [oemIndex, "franchises", franchiseIndex, "dealerCode"])}
								onActionUpdate={(text) => handleFranchiseInputTextChange({ dealerCode: text })}
								disabled={isPending}
							/>
						</Box>
						<Box className="w-[50px]  flex items-center mt-[22px]">
							<SimpleDoubleChecking
								className={"btn-delete"}
								color={"var(--gray-blue-color)"}
								confirmationButtonColor={"var(--gray-blue-color)"}
								disabled={isPending || oem.franchises.length === 1}
								icon={<CloseRoundedIcon fontSize="small" />}
								iconHover={<CloseRoundedIcon fontSize="small" />}
								confirmationIcon={<SvgIcon className="!text-[25px] pl-[5px] pt-[2px]" component={TrashIcon} />}
								onDeleteAction={handleDeleteFranchise}
								size="small"
								sx={{ marginTop: "-3px" }}
							/>
						</Box>
					</Box>
					<Box className="flex flex-wrap justify-start">
						{franchise.franchise && franchise.area && getAreaColumnInput(metaAreas, franchise.area)}
					</Box>
					{updateFranchiseWarning && (
						<Box className="flex text-warning items-center">
							<WarningIcon className="text-warning mr-[4px] mb-[1px]" height={15} width={15} />
							<Typography className="!font-sans">
								Changes have been made to the franchise(s). Please revise all other fields to ensure they are still correct
								and&nbsp;
								<Typography
									className="!font-sans underline cursor-pointer"
									component={"span"}
									onClick={handleUpdateFranchiseWarning}
								>
									CONFIRM
								</Typography>{" "}
								changes.
							</Typography>
						</Box>
					)}
				</Box>
			</>
		);
	}
);

export default FranchisesLayouts;
